import { useCallback } from 'react'
import { useToastContext, type ToastData } from './ToastProvider'

export interface UseToastReturn {
  toast: {
    success: (title: string, description?: string) => string
    error: (title: string, description?: string) => string
    loading: (title: string, description?: string) => string
    info: (title: string, description?: string) => string
  }
  dismiss: (id: string) => void
  update: (id: string, updates: Partial<Omit<ToastData, 'id'>>) => void
}

export const useToast = (): UseToastReturn => {
  const { addToast, removeToast, updateToast } = useToastContext()

  const success = useCallback((title: string, description?: string) => {
    if (!title?.trim()) return ''
    return addToast({
      title: title.trim(),
      description: description?.trim(),
      variant: 'success'
    })
  }, [addToast])

  const error = useCallback((title: string, description?: string) => {
    if (!title?.trim()) return ''
    return addToast({
      title: title.trim(),
      description: description?.trim(),
      variant: 'error'
    })
  }, [addToast])

  const loading = useCallback((title: string, description?: string) => {
    if (!title?.trim()) return ''
    return addToast({
      title: title.trim(),
      description: description?.trim(),
      variant: 'loading',
      duration: 0
    })
  }, [addToast])

  const info = useCallback((title: string, description?: string) => {
    if (!title?.trim()) return ''
    return addToast({
      title: title.trim(),
      description: description?.trim(),
      variant: 'info'
    })
  }, [addToast])

  const dismiss = useCallback((id: string) => {
    removeToast(id)
  }, [removeToast])

  const update = useCallback((id: string, updates: Partial<Omit<ToastData, 'id'>>) => {
    updateToast(id, updates)
  }, [updateToast])

  return {
    toast: {
      success,
      error,
      loading,
      info
    },
    dismiss,
    update
  }
}